import React, { useMemo, useState } from 'react';
import { 
  Grid, 
  GridColumn,
  GridItemChangeEvent,
  GridCellProps 
} from "@progress/kendo-react-grid";
import { Endpoints, EndpointStatus, Groups } from '../../../../Models/Assets/AssetTemplateReadDto';
import { ComboBox, ComboBoxFilterChangeEvent, DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { filterBy, FilterDescriptor } from "@progress/kendo-data-query";
import { RequirementReadDto } from '../../../../Models/Requirement/RequirementReadDto';
import { enMessages } from '../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';

interface Props {
  endpoints?: Endpoints[];
  assetData?: any;
  selectedGroupId?: string | null;
  onEndpointsChange?: (endpoints: Endpoints[]) => void;
  serialNumbers?: { id: string; serialNumber: string }[];
  requirements?: RequirementReadDto[];
  groups?: Groups[];
}

/** Unique key per row: use id when set, else temp key with index to avoid collisions (ADD mode - empty/duplicate ids) */
function getDataItemKey(item: Endpoints, index?: number): string {
  if (item.id) return item.id;
  return index !== undefined ? `temp-ep-${index}` : `temp-ep-${item.groupId}-${item.name}`;
}

const statusOptions: { label: string; value: EndpointStatus }[] = [
  { label: 'Enabled', value: 'Active' },
  { label: 'Disabled', value: 'Inactive' }
];

function endpointStatus(ep: Endpoints): EndpointStatus {
  const status = (ep as Endpoints & { status?: EndpointStatus }).status;
  return status ?? 'Inactive';
}

/** ComboBox with filter - exactly as Kendo docs: data state + filterData + onFilterChange */
function FilterableSerialComboBox(
  props: {
    data: { id: string; serialNumber: string }[];
    value: { id: string; serialNumber: string };
    onChange: (value: { id: string; serialNumber: string }) => void;
    disabled?: boolean;
  }
) {
  const [data, setData] = useState(props.data.slice());

  const filterData = (filter: FilterDescriptor) => {
    const allData = props.data.slice();
    return filterBy(allData, { ...filter, field: "serialNumber" });
  };

  const filterChange = (event: ComboBoxFilterChangeEvent) => {
    setData(filterData(event.filter));
  };

  return (
    <ComboBox
      data={data}
      dataItemKey="id"
      textField="serialNumber"
      filterable={true}
      onFilterChange={filterChange}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value ?? { id: '', serialNumber: '' })}
      style={{ width: "100%" }}
      popupSettings={{ width: 180 }}
      disabled={props.disabled}
    />
  );
}

const EndpointMappingTableComponent: React.FC<Props> = ({ 
  assetData = {}, 
  selectedGroupId,
  onEndpointsChange,
  endpoints = [],
  serialNumbers = [],
  requirements = [],
  groups
}) => {
  const localizationService = useLocalization();

  // Filter endpoints based on selected group, then sort by name
  const filteredEndpoints = useMemo(() => {
    const list = selectedGroupId === '0'
      ? endpoints
      : selectedGroupId === null
        ? []
        : endpoints.filter((endpoint: Endpoints) => endpoint.groupId === selectedGroupId);
    return [...list].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }, [endpoints, selectedGroupId]);

  // Always editable - mark all endpoints with inEdit: true and unique _gridKey
  const editableEndpoints = useMemo(() => {
    return filteredEndpoints.map((endpoint, index) => ({
      ...endpoint,
      inEdit: true,
      _gridKey: getDataItemKey(endpoint, index)
    }));
  }, [filteredEndpoints]);

  const findEndpointByGridKey = (key: string) => {
    const filteredIndex = filteredEndpoints.findIndex((ep, i) => getDataItemKey(ep, i) === key);
    return filteredIndex >= 0 ? filteredEndpoints[filteredIndex] : null;
  };

  // Get selected group and its name
  const selectedGroup = groups?.find((group: Groups) => group.id === selectedGroupId) 
    || assetData.groups?.find((group: Groups) => group.id === selectedGroupId);
  const selectedGroupName = selectedGroup?.name || 'All Groups';
  const isGroupInactiveOrDeleted = selectedGroup && ((selectedGroup as Groups & { status?: string }).status === 'Inactive' || (selectedGroup as Groups & { status?: string }).status === 'Deleted');
  const isSelectedGroupDisabled = selectedGroupId && selectedGroupId !== '0' && !!isGroupInactiveOrDeleted;

  const handleItemChange = (event: GridItemChangeEvent) => {
    const key = (event.dataItem as Endpoints & { _gridKey?: string })._gridKey;
    const endpointToUpdate = key ? findEndpointByGridKey(key) : null;
    const newEndpoints = endpointToUpdate
      ? endpoints.map((ep: Endpoints) =>
          ep === endpointToUpdate ? { ...ep, [event.field!]: event.value } : ep
        )
      : endpoints;
    onEndpointsChange?.(newEndpoints);
  };


  const notConfigured = localizationService.toLanguageString('custom.notConfigured', enMessages.custom.notConfigured);

  const SerialNumberCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const isDisabled = endpointStatus(dataItem as Endpoints) === 'Inactive';

    const key = (dataItem as Endpoints & { _gridKey?: string })._gridKey;
    const currentEndpoint = key ? findEndpointByGridKey(key) : null;
    const currentSerial = dataItem.endpointDeviceSerialNumber ?? '';
    const currentId = dataItem.endpointDeviceId ?? '';

    // Serial numbers already assigned to OTHER endpoints (exclude current row)
    const assignedToOtherIds = new Set(
      endpoints
        .filter(ep => ep !== currentEndpoint)
        .map(ep => ep.endpointDeviceId)
        .filter(Boolean)
    );

    // Filter: Not Configured + available serials (exclude assigned to others, but keep current row's selection)
    const baseSerials = [
      { id: '', serialNumber: notConfigured },
      ...serialNumbers.filter(sn => !assignedToOtherIds.has(sn.id) || sn.id === currentId)
    ];
    const currentValue = { id: currentId, serialNumber: currentSerial || notConfigured };
    const baseAvailable = baseSerials.some(sn => sn.id === currentId)
      ? baseSerials
      : (currentId ? [currentValue, ...baseSerials] : baseSerials);

    const valueForDropdown = baseAvailable.find((sn: { id: string; serialNumber: string }) => sn.id === currentId)
      ?? baseAvailable.find((sn: { id: string; serialNumber: string }) => sn.serialNumber === currentSerial)
      ?? baseAvailable[0];

    return (
      <td>
        <FilterableSerialComboBox
          key={`${key}-${baseAvailable.map((s) => s.id).join(",")}`}
          data={baseAvailable}
          value={valueForDropdown}
          disabled={isDisabled}
          onChange={(val) => {
            const endpointToUpdate = findEndpointByGridKey((dataItem as Endpoints & { _gridKey?: string })._gridKey ?? '');
            if (!endpointToUpdate) return;
            onEndpointsChange?.(endpoints.map(ep =>
              ep === endpointToUpdate
                ? {
                    ...ep,
                    endpointDeviceSerialNumber: val?.serialNumber ?? '',
                    endpointDeviceId: val?.id ?? ''
                  }
                : ep
            ));
          }}
        />
      </td>
    );
  };

  const RequirementCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const isDisabled = endpointStatus(dataItem as Endpoints) === 'Inactive';

    // Add 'No Requirement' option at the top
    const requirementsWithNone = [{ name: 'No Requirement', id: '' }, ...requirements];

    return (
      <td>
        <DropDownList
          style={{ width: "100%" }}
          data={requirementsWithNone}
          disabled={isDisabled}
          dataItemKey="name"
          textField="name"
          value={requirementsWithNone.find(r => r.name === (dataItem.requirementName || 'No Requirement')) || requirementsWithNone[0]}
          onChange={e => {
            const key = (dataItem as Endpoints & { _gridKey?: string })._gridKey;
            const endpointToUpdate = key ? findEndpointByGridKey(key) : null;
            if (!endpointToUpdate) return;
            const isNone = e.target.value.id === '';
            const newEndpoints = endpoints.map(endpoint =>
              endpoint === endpointToUpdate
                ? {
                    ...endpoint,
                    requirementName: isNone ? '' : e.target.value.name,
                    requirementId: isNone ? '' : e.target.value.id
                  }
                : endpoint
            );
            onEndpointsChange?.(newEndpoints);
          }}
        />
      </td>
    );
  };

  const statusCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const status = endpointStatus(dataItem as Endpoints);
    const handleDropDownChange = (e: DropDownListChangeEvent) => {
      handleItemChange({
        dataItem: dataItem,
        field: "status",
        value: e.target.value.value
      } as GridItemChangeEvent);
    };
    return (
      <td>
        <DropDownList
          style={{ width: "100%" }}
          data={statusOptions}
          textField="label"
          dataItemKey="value"
          value={statusOptions.find(opt => opt.value === status) ?? statusOptions.find(opt => opt.value === 'Inactive')}
          onChange={handleDropDownChange}
        />
      </td>
    );
  };

  if (isSelectedGroupDisabled) {
    return (
      <div className="flex flex-col h-full">
        <div className="text-xl font-bold mb-2">
          {localizationService.toLanguageString('custom.endpointMapping', enMessages.custom.endpointMapping)} ({selectedGroupName})
        </div>
        <div className="flex-1 flex items-center justify-center text-gray-500 font-medium">
          {localizationService.toLanguageString('custom.groupDisabled', enMessages.custom.groupDisabled)}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
        <div className="flex justify-between align-middle mb-2">
        <div className="text-xl font-bold">{localizationService.toLanguageString('custom.endpointMapping', enMessages.custom.endpointMapping)} ({selectedGroupName})</div>
      </div>
      <div className="flex-1 min-h-0 overscroll-contain">
        <Grid
          data={editableEndpoints}
          style={{ height: '100%' }}
          dataItemKey="_gridKey"
          onItemChange={handleItemChange}
          editable={true}
          editField="inEdit"
        >
          <GridColumn field="name" title={localizationService.toLanguageString('custom.name', enMessages.custom.name)} editable={false} />
          <GridColumn 
            field="endpointDeviceSerialNumber" 
            title={localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)} 
            cell={SerialNumberCell}
          />
          <GridColumn 
            field="requirementName"
            title={localizationService.toLanguageString('custom.requirement', enMessages.custom.requirement)} 
            cell={RequirementCell}
          />
          <GridColumn 
            field="status" 
            title={localizationService.toLanguageString('custom.status', enMessages.custom.status)}
            cells={{
              data: statusCell
            }}
          />
        </Grid>
      </div>
    </div>
  );
};

export default EndpointMappingTableComponent; 